import * as Phaser from "phaser"
import { GAME_HEIGHT, GAME_WIDTH } from "../../data/constants"
import type { EnemyData, GameSprite } from "../../types"
import {
  type EnemyState,
  type EnemyStateContext,
  StatefulEnemyRuntime,
  clampEnemyY,
} from "../enemy-system"

type DwarfPhase = "approach" | "shoot" | "teleport"

const DWARF_SHOOT_DURATION_MS = 900
const DWARF_TELEPORT_DURATION_MS = 120
const DWARF_TELEPORT_STEP_PX = 180
const DWARF_MAX_TELEPORTS = 5

export class DwarfEnemy extends StatefulEnemyRuntime<DwarfPhase> {
  public constructor(sprite: GameSprite, enemyData: EnemyData) {
    const states: EnemyState<DwarfPhase>[] = [
      {
        name: "approach",
        behaviorState: "flying",
        animationState: "flying",
        enter: (context) => this.playDwarfAnimation(context),
        update: (context) => this.updateApproach(context),
      },
      {
        name: "shoot",
        behaviorState: "attacking",
        animationState: "attacking",
        enter: (context) => this.enterShoot(context),
        update: (context) => this.updateShoot(context),
      },
      {
        name: "teleport",
        behaviorState: "panicked",
        animationState: "panicked",
        canFire: false,
        enter: (context) => this.enterTeleport(context),
        update: (context) => this.updateTeleport(context),
      },
    ]

    super(sprite, enemyData, "approach", states)
  }

  private playDwarfAnimation(context: EnemyStateContext<DwarfPhase>): void {
    if (context.scene.anims.exists("anim_dwarf")) {
      context.sprite.play("anim_dwarf", true)
    }
  }

  private updateApproach(
    context: EnemyStateContext<DwarfPhase>,
  ): DwarfPhase | null {
    const { sprite, enemyData, time, delta, playerY, scrollCompensation } =
      context
    const spec = context.enemyDef?.movementSpec
    if (spec?.action !== "track-player-y") return null

    const homeX = enemyData.homeX ?? sprite.x
    const homeY = enemyData.homeY ?? sprite.y
    const sweep = Math.sin(time / 180) * 10
    const pull = (playerY - homeY) * 0.12
    const targetY = clampEnemyY(homeY + sweep + pull)

    const trackFactor = 1 - (1 - 0.08) ** ((delta / 16.6) * spec.trackSpeed)
    sprite.y = Phaser.Math.Linear(sprite.y, targetY, trackFactor)

    enemyData.vx = scrollCompensation - 1.25

    const stopX = Math.min(GAME_WIDTH - 220, homeX - 80)
    if (sprite.x <= stopX) {
      sprite.x = stopX + Math.sin(time / 320) * 4
      return "shoot"
    }
    return null
  }

  private enterShoot(context: EnemyStateContext<DwarfPhase>): void {
    context.sprite.setAlpha(1)
    context.enemyData.lastShot = context.enemyData.shootCooldown
    this.playDwarfAnimation(context)
  }

  private updateShoot(
    context: EnemyStateContext<DwarfPhase>,
  ): DwarfPhase | null {
    const { sprite, enemyData, stateTime, scrollCompensation } = context
    const spec = context.enemyDef?.movementSpec
    if (spec?.action !== "track-player-y") return null

    enemyData.vx = scrollCompensation - 0.55
    sprite.y = Phaser.Math.Linear(
      sprite.y,
      (enemyData.homeY ?? sprite.y) + Math.sin(stateTime / 140) * 6,
      0.08,
    )

    if (stateTime >= DWARF_SHOOT_DURATION_MS) {
      return "teleport"
    }
    return null
  }

  private enterTeleport(context: EnemyStateContext<DwarfPhase>): void {
    const { sprite, enemyData, scrollCompensation } = context
    const nextTeleportCount = (enemyData.dwarfTeleportCount ?? 0) + 1
    enemyData.dwarfTeleportCount = nextTeleportCount

    if (nextTeleportCount >= DWARF_MAX_TELEPORTS) {
      sprite.destroy()
      return
    }

    const nextX = Phaser.Math.Between(GAME_WIDTH / 2 + 40, GAME_WIDTH - 80)
    const nextY = Phaser.Math.Between(90, GAME_HEIGHT - 90)

    sprite.setAlpha(0)
    sprite.setPosition(nextX, nextY)
    enemyData.vx = scrollCompensation - 1.1
    enemyData.vy = 0
  }

  private updateTeleport(
    context: EnemyStateContext<DwarfPhase>,
  ): DwarfPhase | null {
    const { sprite, enemyData, stateTime, scrollCompensation } = context
    const spec = context.enemyDef?.movementSpec
    if (spec?.action !== "track-player-y") return null

    enemyData.vx = scrollCompensation - 1.1
    if (stateTime >= DWARF_TELEPORT_DURATION_MS) {
      sprite.setAlpha(1)
      return "shoot"
    }
    return null
  }
}
